import { collectionMethods } from './collectionMethods.js';

const BASE_PATH = '/dealerships';

/**
 * Dealerships — the rooftops that buy and sell.
 *
 * A Dealership belongs to a Dealership Operation, may hold its own legal
 * identity and payer, and carries the claims that feed its good standing.
 *
 * Every route below writes its own path out in full. Several of them appear on
 * other nouns word for word, and that repetition is deliberate: a reader asking
 * what `setLegalCompany` reaches should find the answer here rather than in a
 * factory that assembles it from a base path passed in from somewhere else.
 */
export function dealershipMethods({ sendRequest, buildUrl }) {
    return {
        ...collectionMethods({ basePath: BASE_PATH, sendRequest, buildUrl }),

        /**
         * The Company this Dealership legally is.
         *
         * @param {string|null} companyId - `null` clears it, which falls the
         *   resolution through to the Operation rather than leaving the
         *   Dealership without a legal identity.
         */
        async setLegalCompany(id, companyId) {
            return await sendRequest(
                buildUrl(`${BASE_PATH}/${encodeURIComponent(id)}/legal-company`),
                'PUT',
                { companyId }
            );
        },

        /**
         * Who pays, and under which Stripe subscription.
         *
         * The server takes one of two bodies here and nothing between them:
         * `{ companyId, stripeSubscriptionId }` to name a payer, or
         * `{ companyId: null }` alone to clear. So a clear sends one key rather
         * than two, and a caller that passes a subscription with a null payer
         * gets the refusal it earned rather than a silently dropped field.
         *
         * @param {string|null} companyId
         * @param {string} [stripeSubscriptionId] - Required whenever a payer is named.
         */
        async setBillingCompany(id, companyId, stripeSubscriptionId) {
            const body = { companyId };
            if (stripeSubscriptionId !== undefined) {
                body.stripeSubscriptionId = stripeSubscriptionId;
            }
            return await sendRequest(
                buildUrl(`${BASE_PATH}/${encodeURIComponent(id)}/billing-company`),
                'PUT',
                body
            );
        },

        /**
         * The Operation this Dealership belongs to.
         *
         * Not nullable, unlike the Company relationships: a Dealership with no
         * Operation has nothing to resolve its legal identity or its payer
         * through, so the server refuses to leave one that way.
         */
        async setOperation(id, operationId) {
            return await sendRequest(
                buildUrl(`${BASE_PATH}/${encodeURIComponent(id)}/operation`),
                'PUT',
                { operationId }
            );
        },

        /**
         * Set one policy. The shape is the policy's own — the server stores it
         * as given and the application validates it against the key.
         */
        async setPolicy(id, policyName, policy) {
            return await sendRequest(
                buildUrl(`${BASE_PATH}/${encodeURIComponent(id)}/policies/${encodeURIComponent(policyName)}`),
                'PUT',
                policy
            );
        },

        /** Remove one policy. */
        async clearPolicy(id, policyName) {
            return await sendRequest(
                buildUrl(`${BASE_PATH}/${encodeURIComponent(id)}/policies/${encodeURIComponent(policyName)}`),
                'DELETE'
            );
        },

        /**
         * Replace the claim set whole.
         *
         * A replace rather than an add and a remove, because that is what the
         * route is. A caller adding one claim sends the others back.
         *
         * @param {Array<{ claimType: string, claimProperties: Object }>} claims
         */
        async setClaims(id, claims) {
            return await sendRequest(
                buildUrl(`${BASE_PATH}/${encodeURIComponent(id)}/claims`),
                'PUT',
                { claims }
            );
        },

        /**
         * A page of the Auction Houses this Dealership is registered at.
         *
         * Answers the same `{ meta, documents }` page the list and search routes
         * answer, and pages the same way.
         */
        async listAuctionHouses(id, { page, pageSize } = {}) {
            return await sendRequest(
                buildUrl(`${BASE_PATH}/${encodeURIComponent(id)}/auction-houses`, { page, pageSize }),
                'GET'
            );
        }
    };
}

export default dealershipMethods;
